import React, { useEffect, useMemo, useState } from "react";
import { useComponentEditorContext } from "./ComponentEditorContext";
import { ContainerSizeInfo } from "./ContainerSizeInfo";
import { Box, Modal, SelectChangeEvent, TextField } from "@mui/material";
import { BackgroundSelector } from "./BackgroundSelector";
import { useObjectImages } from "./CustomHooks";
import { useModalEditorDisplayContext } from "./ModalEditorContext";
import { useTreeDataContext, useTreeModifyContext } from "./TreeDataContext";
import { SaveAndCloseButton } from "./SaveAndCloseButton";
import { ZoomControls } from "./ZoomControls";

export const ClipPathEditor: React.FC<{
  showModal: boolean;
  modalContextKey: string[];
  modalContextValue: any;
}> = ({ showModal, modalContextKey, modalContextValue }) => {
  const { onCloseModal } = useModalEditorDisplayContext();
  const { saveChangesAtPath } = useTreeModifyContext();
  const { projectName, projectInfo, getVisual } = useComponentEditorContext();
  const { treeData } = useTreeDataContext();
  const [myModalContextValue, setMyModalContextValue] = useState(modalContextValue as string);
  const { possibleBackgroundImages, selectedBackgroundImage, setSelectedBackgroundImage } = useObjectImages(
    modalContextKey,
    treeData,
    projectName,
    projectInfo,
  );
  const [zoom, setZoom] = useState(1);
  const [anyChangesMade, setAnyChangesMade] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (anyChangesMade) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [anyChangesMade, showModal]);

  const backgroundImageValue = useMemo(() => {
    if (selectedBackgroundImage) {
      return possibleBackgroundImages.find(
        (image) =>
          (image.type === "src" && image.value === selectedBackgroundImage) ||
          (image.type === "reactImg" && image.name === selectedBackgroundImage),
      );
    }
  }, [possibleBackgroundImages, selectedBackgroundImage]);

  const handleBackgroundChange = (event: SelectChangeEvent) => {
    setSelectedBackgroundImage(event.target.value);
  };

  function handleCloseModal() {
    saveChangesAtPath(myModalContextValue, modalContextKey);
    onCloseModal();
  }

  function applyChange(event: React.ChangeEvent<HTMLInputElement>) {
    setMyModalContextValue(event.target.value);
    setAnyChangesMade(true);
  }

  if (!showModal) {
    return <></>;
  }

  return (
    <Modal
      open={showModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box className="large-modal">
        <Box className="editor-layout">
          <Box className="control-panel control-panel-layout">
            <ZoomControls setZoom={setZoom} />
            <BackgroundSelector
              value={selectedBackgroundImage}
              onChange={handleBackgroundChange}
              possibleBackgroundImages={possibleBackgroundImages}
            />
            <TextField multiline value={myModalContextValue} onChange={applyChange} />
            <Box className="flex-filler"></Box> {/* Spacer to fill empty space*/}
            <SaveAndCloseButton onClick={handleCloseModal} />
          </Box>
          <Box className={"image-display"}>
            {getVisual(
              backgroundImageValue,
              (newRef) => {},
              {
                display: "grid",
                width: `fit-content`,
                height: `fit-content`,
              },
              {
                clipPath: myModalContextValue,
                padding: `20px`,
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
                alignSelf: `safe center`,
                justifySelf: `safe center`,
              },
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
